/**
 * Barre supérieure — responsive et sans débordement : le titre absorbe la contrainte
 * d'espace (flex-1 + truncate), tout le reste est shrink-0. Le sélecteur d'espace y est
 * TOUJOURS monté et visible (contexte global + repérable par les tests sur tout viewport).
 * Les repères d'identité (current-user/role/org) restent dans le DOM (masqués aux petits
 * viewports) pour les assertions de contenu.
 */
import type { JSX } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Bell, ChevronDown, CircleHelp, Inbox, Search } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar'
import { CreateMenu } from '@/components/CreateMenu'
import { UserMenu } from '@/components/UserMenu'
import type { Session } from '@/auth/session'
import type { NavigationMode } from '@/lib/navigationMode'
import { currentNavItem } from '@/lib/nav'
import { api } from '@/api/client'
import { FilAriane } from '@/components/FilAriane'
import { ProchaineActionProjet } from '@/components/ProchaineActionProjet'
import { useWorkspace } from '@/hooks/useWorkspace'
import { useNomOrganisation } from '@/hooks/useProfilOrganisation'
import { useT } from '@/i18n/language'
import { Brand } from '@/components/Brand'
import { BarreContexte } from '@/components/contexte/BarreContexte'
import { useVarianteCoquille } from '@/hooks/useVarianteCoquille'
import { APP_VERSION, BUILD_DATE } from '@/lib/version'
import {
  Popover, PopoverContent, PopoverDescription, PopoverHeader, PopoverTitle, PopoverTrigger,
} from '@/components/ui/popover'

type TopBarTranslation = (key: string, fallback: string, variables?: Record<string, string>) => string

/** Résout l'aide sans réutiliser une description française dans une session anglaise. */
export function contextualHelpForRoute(pathname: string, t: TopBarTranslation): {
  title: string
  description: string
} {
  const item = currentNavItem(pathname)
  const generic = t('app.helpDescription', 'Repères et prochaine action dans le contexte courant.')
  if (!item) return { title: t('app.help', 'Aide'), description: generic }
  return {
    title: t(`nav.${item.path}`, item.label),
    description: t(`navdesc.${item.path}`, generic),
  }
}

/** Information produit stable : l'installation locale ne vend ni abonnement ni paiement. */
export function localSupportAndCommercialBoundary(t: TopBarTranslation): {
  channel: string
  boundary: string
  safety: string
} {
  return {
    channel: t('app.localSupportChannel',
      'Support local : contactez l’opérateur RunOps de votre organisation via son canal interne.'),
    boundary: t('app.commercialBoundary',
      'Frontière commerciale : cette installation on-premise ne propose ni abonnement, ni paiement, ni solde acheté ; les quotas expriment uniquement la gouvernance de capacité.'),
    safety: t('app.localSupportSafety',
      'Pour le diagnostic, transmettez l’identifiant de corrélation et le contexte non sensible ; ne transmettez jamais de jeton, secret ou contenu de document.'),
  }
}

/**
 * Accès de la barre haute à la Boîte d'attention et aux Notifications (§ 4.3.1), avec leurs
 * compteurs : même clé de cache que l'écran (l'écran et la barre ne peuvent pas afficher deux
 * nombres différents). Déplacés de la barre latérale, qui ne porte plus que le niveau courant.
 */
function AccesAttention(): JSX.Element {
  const { t } = useT()
  const { activeId: workspaceId } = useWorkspace()
  const attention = useQuery({
    queryKey: ['hitl-pending', workspaceId],
    queryFn: () => api.listHitlPendingDecisions(workspaceId!),
    enabled: Boolean(workspaceId),
    refetchInterval: 20000,
    refetchIntervalInBackground: false,
  })
  const nonLues = useQuery({
    queryKey: ['notifications-unread', workspaceId],
    queryFn: () => api.unreadNotificationsCount(workspaceId!),
    enabled: Boolean(workspaceId),
    refetchInterval: 20000,
    refetchIntervalInBackground: false,
  })
  const nAttention = attention.data?.length ?? 0
  const nNonLues = nonLues.data?.count ?? 0
  const pastille = (n: number, testid: string): JSX.Element | null => n > 0 ? (
    <span data-testid={testid} aria-hidden="true"
          className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--color-compteur)] px-[5px] text-[0.6875rem] font-semibold tabular-nums text-[var(--color-compteur-text)]">
      {n > 99 ? '99+' : n}
    </span>
  ) : null
  return (
    <>
      <Button asChild variant="outline" size="sm" className="relative gap-2">
        <Link to="/attention" data-testid="barre-attention"
              aria-label={t('app.attentionAria', 'Boîte d’attention : {{n}} décision(s) en attente', { n: String(nAttention) })}>
          <Inbox className="size-5" aria-hidden="true" />
          <span className="hidden xl:inline">{t('app.attention', 'Boîte d’attention')}</span>
          {pastille(nAttention, 'attention-count')}
        </Link>
      </Button>
      <Button asChild variant="outline" size="icon" className="relative hidden md:inline-flex">
        <Link to="/notifications" data-testid="barre-notifications"
              aria-label={t('app.notificationsAria', 'Notifications : {{n}} non lue(s)', { n: String(nNonLues) })}>
          <Bell className="size-5" aria-hidden="true" />
          {pastille(nNonLues, 'notifications-badge')}
        </Link>
      </Button>
    </>
  )
}

function ContextualHelp(): JSX.Element {
  const { pathname } = useLocation()
  const { t } = useT()
  const { workspaces, activeId } = useWorkspace()
  const project = workspaces.find((workspace) => workspace.workspace_id === activeId)
  const help = contextualHelpForRoute(pathname, t)
  const boundary = localSupportAndCommercialBoundary(t)

  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" data-testid="context-help-trigger"
                aria-label={t('app.helpAria', 'Ouvrir l’aide contextuelle')}>
          <CircleHelp className="size-5" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" side="bottom"
        className="w-[min(22rem,calc(100vw-1rem))] p-0" data-testid="context-help-panel">
        <PopoverHeader className="border-b p-4">
          <PopoverTitle>{t('app.helpTitle', 'Aide contextuelle')}</PopoverTitle>
          <PopoverDescription>
            {t('app.helpFor', 'Vous êtes dans « {{screen}} ».', { screen: help.title })}
          </PopoverDescription>
        </PopoverHeader>
        <div className="space-y-3 p-4 text-sm">
          <p className="text-muted-foreground">{help.description}</p>
          <section className="rounded-md border bg-muted/20 p-3 text-xs"
                   data-testid="local-support-commercial-boundary">
            <p className="font-medium">{boundary.channel}</p>
            <p className="mt-2">{boundary.boundary}</p>
            <p className="mt-2 text-muted-foreground">{boundary.safety}</p>
          </section>
          {project ? (
            <p className="rounded-md bg-muted/40 px-3 py-2 text-xs">
              <span className="font-medium">{t('app.helpProject', 'Projet actif :')}</span>{' '}
              <span className="break-words">{project.name}</span>
            </p>
          ) : null}
          <div className="grid gap-2">
            <Link to="/guide" className="rounded-md border p-3 transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring">
              <span className="font-medium">{t('app.helpJourneyTitle', 'Revenir au parcours')}</span>
              <span className="mt-1 block text-xs text-muted-foreground">
                {t('app.helpJourneyDesc', 'Retrouver les étapes idée → publication et la prochaine action.')}
              </span>
            </Link>
            <Link to="/projet?vue=processus" className="rounded-md border p-3 transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring">
              <span className="font-medium">{t('app.helpProcessTitle', 'Voir le processus effectif')}</span>
              <span className="mt-1 block text-xs text-muted-foreground">
                {t('app.helpProcessDesc', 'Vérifier les prérequis, rôles et gates de ce projet.')}
              </span>
            </Link>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Pastille de contexte — téléphone (§ 4.8, planche SelecteurTelephone) : le contexte le plus
 * profond (le projet ouvert), qui ouvre le sélecteur en FEUILLE plein écran.
 */
export function PastilleContexte({ ouvert, onOuvrir }: { ouvert: boolean; onOuvrir: () => void }): JSX.Element {
  const { t } = useT()
  const { workspaces, activeId } = useWorkspace()
  const projet = workspaces.find((w) => w.workspace_id === activeId)
  const nom = projet?.name ?? t('contexte.aucunProjetChoisi', 'Aucun projet choisi')
  return (
    <button type="button" onClick={onOuvrir} data-testid="pastille-contexte"
            aria-haspopup="dialog" aria-expanded={ouvert}
            aria-label={projet ? t('contexte.pastilleAria', 'Projet : {{nom}} — changer', { nom })
              : t('contexte.pastilleAucun', 'Choisir un projet')}
            className="inline-flex min-h-11 min-w-0 flex-1 items-center gap-1.5 rounded-full border px-3 text-sm font-medium hover:bg-muted md:hidden">
      <span className="size-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
      <span className="min-w-0 truncate">{nom}</span>
      <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </button>
  )
}

/** Bouton Menu : « Ouvrir le menu » au téléphone (tiroir) ET en tablette (panneau complet :
 *  le rail n'expose que les groupes, pas le pied du panneau — langue, version, profil ; masquer
 *  ce bouton de 768 à 1023 px rendait ces fonctions inatteignables, FND-V050-077) ; bascule du
 *  panneau au bureau (inchangé). */
export function BoutonMenu(): JSX.Element {
  const { t } = useT()
  const { variante, openMobile } = useSidebar()
  const modal = variante !== 'bureau'
  return (
    <SidebarTrigger className="size-11 shrink-0 text-muted-foreground lg:size-10"
                    data-testid="bouton-menu"
                    {...(modal ? {
                      'aria-label': t('nav.ouvrirMenu', 'Ouvrir le menu'),
                      'aria-expanded': openMobile,
                      'aria-haspopup': 'dialog' as const,
                    } : {})} />
  )
}

export function TopBar({
  session, onOpenCommand, onNewProject, navigationMode, onNavigationModeChange, navigationModeError,
  navigationModeConflict, onConfirmNavigationModeFreshValue, contexteOuvert = false, onOuvrirContexte,
}: {
  session: Session
  onOpenCommand: () => void
  onNewProject: () => void
  navigationMode: NavigationMode
  onNavigationModeChange: (mode: NavigationMode) => void
  navigationModeError: string | undefined
  navigationModeConflict?: { mode: NavigationMode } | null | undefined
  onConfirmNavigationModeFreshValue?: (() => void) | undefined
  /** Téléphone : la feuille du sélecteur de contexte est ouverte. */
  contexteOuvert?: boolean
  /** Téléphone : ouvre la feuille du sélecteur de contexte (absente = pas de pastille). */
  onOuvrirContexte?: (() => void) | undefined
}): JSX.Element {
  const { t } = useT()
  const nomOrganisation = useNomOrganisation(session.organisationId)
  // Bureau (planche BarreHaute) : marque « D DoIt · organisation », sélecteurs programme › projet,
  // puis les commandes ; le fil d'Ariane et la prochaine action passent dans l'en-tête de page.
  const bureau = useVarianteCoquille() === 'bureau'

  return (
    <header data-coquille="barre-haute" data-forme={bureau ? 'bureau' : 'compacte'}
            className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-card px-2 sm:px-3.5">
      {bureau ? (
        <>
          <Link to="/" data-testid="barre-marque"
                aria-label={t('app.marqueAria', 'DoIt — {{org}} : aller à l’accueil', { org: session.organisationId ?? '' })}
                className="inline-flex min-h-11 shrink-0 items-center rounded-lg [&>span]:px-0">
            <Brand />
          </Link>
          {/* Lot ORG : le NOM de l'organisation (pas son identifiant technique) mène à sa fiche. */}
          {session.organisationId ? (
            <Link to="/org/fiche" data-testid="barre-organisation" title={session.organisationId}
                  className="inline-flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-md px-1 text-[0.8125rem] font-normal text-muted-foreground hover:bg-accent hover:underline focus-visible:ring-2 focus-visible:ring-ring">
              · {nomOrganisation ?? session.organisationId}
            </Link>
          ) : (
            <span data-testid="barre-organisation" className="technical-id shrink-0 whitespace-nowrap text-[0.8125rem] font-normal text-muted-foreground">
              · {t('contexte.organisationInconnue', 'organisation inconnue')}
            </span>
          )}
          <span aria-hidden="true" className="mx-1 h-[26px] w-px shrink-0 bg-border" />
          <BarreContexte session={session} variante="barre" />
        </>
      ) : (
        <>
          <BoutonMenu />
          {onOuvrirContexte ? <PastilleContexte ouvert={contexteOuvert} onOuvrir={onOuvrirContexte} /> : null}
          <FilAriane partie="large" />
          <ProchaineActionProjet />
        </>
      )}

      <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-2">
        <Button variant="outline" size="sm" onClick={onOpenCommand}
                className="hidden w-[200px] items-center justify-start gap-2 border-input text-[0.8125rem] font-normal text-muted-foreground lg:inline-flex">
          <Search className="size-4" aria-hidden="true" />
          <span className="grow text-left">{t('app.search', 'Rechercher…')}</span>
          <kbd className="pointer-events-none ml-1 select-none rounded border bg-transparent px-1.5 font-mono text-[0.6875rem] font-normal">
            ⌘K
          </kbd>
        </Button>
        <Button variant="outline" size="icon" onClick={onOpenCommand}
                aria-label={t('app.searchAria', 'Rechercher (Ctrl+K)')} className="hidden md:inline-flex lg:hidden">
          <Search className="size-5" aria-hidden="true" />
        </Button>

        <AccesAttention />

        {/* Téléphone (§ 4.9, CoquilleTelephone) : Menu, contexte, Boîte d'attention, Compte ;
            Notifications, Créer et l'Aide sont au pied du tiroir. */}
        <div className="hidden md:block"><CreateMenu onNewProject={onNewProject} /></div>
        <div className="hidden md:block"><ContextualHelp /></div>

        {/* Les trois bascules d'affichage (thème, densité, identifiants techniques) tenaient
            chacune une place permanente ici. Ce sont des préférences réglées une fois : elles
            coûtaient de la place à ce qu'on utilise en permanence — le contexte de travail. */}

        <UserMenu session={session} navigationMode={navigationMode}
                  onNavigationModeChange={onNavigationModeChange} navigationModeError={navigationModeError}
                  navigationModeConflict={navigationModeConflict}
                  onConfirmNavigationModeFreshValue={onConfirmNavigationModeFreshValue} />
        {bureau ? (
          <span data-testid="barre-version" className="shrink-0 whitespace-nowrap text-right font-mono text-[0.6875rem] leading-tight text-muted-foreground"
                title={BUILD_DATE
                  ? t('app.versionDeployeeLe', 'Version déployée {{version}} — déployée le {{date}}', { version: APP_VERSION, date: BUILD_DATE })
                  : t('app.versionDeployee', 'Version déployée {{version}}', { version: APP_VERSION })}>
            DoIt {APP_VERSION}{BUILD_DATE ? <><br />{t('app.deployeLe', 'déployé le')} {BUILD_DATE}</> : null}
          </span>
        ) : null}
      </div>
    </header>
  )
}
